import { CommonModule } from '@angular/common';
import { Component, ElementRef, EventEmitter, HostListener, Input, OnInit, Output, ViewChild } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { RouterLink } from '@angular/router';
import { TranslocoPipe, TranslocoService } from '@jsverse/transloco';

export interface DropdownOption {
  name: string;
  code: string;
}

@Component({
  selector: 'app-custom-dropdown',
  standalone: true,
  imports: [FormsModule, CommonModule, TranslocoPipe, RouterLink],
  templateUrl: './custom-dropdown.component.html',
  styleUrl: './custom-dropdown.component.css'
})
export class CustomDropdownComponent implements OnInit {
  @Input() options: string[] = [];
  @Input() selectedOption: number | undefined;
  @Output() selectedOptionChange = new EventEmitter<number | undefined>();
  @Input() label: string = '';
  @Input() placeholder: string = '';
  @Input() borderColor: string = 'border-border';
  @Input() focusColor: string = 'focus:ring-primary';
  @Input() backgroundColor: string = 'bg-[#FFFFFF]';
  @Input() boxHeight: string = 'min-h-[50px]';
  @Input() disabled: boolean = false;
  @Input() errorMessage: string | null = null;
  @Input() warningMessage: string | null = null;

  @Input() title: string = '';
  @Input() disabledTitle: string | null = null;
  @Input() showOnlyValueWhenDisabled: boolean = false;

  @Input() trailingIcon: string | null = null;
  @Input() trailingIconAlt: string = '';

  @Input() upgradeIncludes: number[] = [];
  @Input() upgradeUrl: string = '/settings/plans';

  @Input() maxLength: number = 32; // Max length display for selected option

  isDropdownOpen = false;
  focusedIndex: number = -1;
  searchBuffer: string = '';
  searchTimeout: any;

  @ViewChild('dropdownMenu') dropdownMenu!: ElementRef;

  constructor(private translocoService: TranslocoService, private eRef: ElementRef) { }

  ngOnInit(): void {
    if (!this.placeholder) {
      this.translocoService
        .selectTranslate('shared.components.please_select')
        .subscribe((translation) => {
          this.placeholder = translation;
        });
    }
  }

  toggleDropdown(): void {
    if (this.disabled) return;
    this.isDropdownOpen = !this.isDropdownOpen;
    if (this.isDropdownOpen) {
      this.focusedIndex = this.selectedOption ?? -1;
      setTimeout(() => this.scrollToFocused(), 0);
    }
  }

  selectOption(index: number): void {
    this.selectedOption = index;
    this.selectedOptionChange.emit(index);
    this.isDropdownOpen = false;
  }

  getSelectedOption(fullText: boolean = false): string {
    const selectedText = this.selectedOption !== undefined
      ? this.options[this.selectedOption]
      : this.placeholder;

    if (!selectedText) {
      return '';
    }

    if ((selectedText.length > this.maxLength) && !fullText) {
      return selectedText.substring(0, this.maxLength) + '...';
    }

    return selectedText;
  }

  get isTextAboveMaxLength(): boolean {
    const selectedText = (this.selectedOption !== undefined ? this.options[this.selectedOption] : this.placeholder) ?? '';

    return (selectedText.length > this.maxLength);
  }

  @HostListener('document:click', ['$event'])
  clickOutside(event: Event): void {
    if (this.isDropdownOpen && !this.eRef.nativeElement.contains(event.target)) {
      this.isDropdownOpen = false;
    }
  }

  handleKeyDown(event: KeyboardEvent): void {
    if (!this.isDropdownOpen && (event.key === 'Enter' || event.key === ' ')) {
      event.preventDefault();
      this.toggleDropdown();
      return;
    }

    if (this.isDropdownOpen) {
      if (event.key.length === 1 && /^[a-zA-Z0-9]$/.test(event.key)) {
        this.jumpToItem(event.key);
        return;
      }

      switch (event.key) {
        case 'ArrowDown':
          event.preventDefault();
          this.focusedIndex = (this.focusedIndex + 1) % this.options.length;
          this.scrollToFocused();
          break;
        case 'ArrowUp':
          event.preventDefault();
          this.focusedIndex = (this.focusedIndex - 1 + this.options.length) % this.options.length;
          this.scrollToFocused();
          break;
        case 'Enter':
          event.preventDefault();
          if (this.focusedIndex >= 0) {
            this.selectOption(this.focusedIndex);
          }
          break;
        case 'Escape':
          this.isDropdownOpen = false;
          break;
      }
    }
  }

  jumpToItem(letter: string): void {
    clearTimeout(this.searchTimeout);

    this.searchBuffer += letter.toLowerCase();
    this.searchTimeout = setTimeout(() => {
      this.searchBuffer = '';
    }, 1000);

    const index = this.options.findIndex(option =>
      option.toLowerCase().startsWith(this.searchBuffer)
    );

    if (index !== -1) {
      this.focusedIndex = index;
      this.scrollToFocused();
    }
  }

  private scrollToFocused(): void {
    if (!this.dropdownMenu) return;

    const items = this.dropdownMenu.nativeElement.querySelectorAll('li');
    if (items.length === 0 || this.focusedIndex === -1) return;

    const focusedItem = items[this.focusedIndex] as HTMLElement;
    focusedItem.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
  }

  get getTitle(): string {
    if (this.disabled && this.disabledTitle != null) {
      return this.disabledTitle;
    }

    return this.title;
  }
}
